import { useApp } from 'contexts/app';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Text } from 'shell/components';
import { SYSTEM_ASSIGNMENT_DEFAULTS } from 'utils/assignmentDefaults';
import { CUSTOM_CONTENT_WORD_COUNT_LIMIT } from 'utils/constants';
import { getHTMLWordCount } from 'utils/helper';
import { SettingsToggleCard, useClearAssignmentSettingError } from './AssignmentSettingsPanelControls';
import InlineCommentsSettingsForm from './InlineCommentsSettingsForm';

const AssignmentInlineCommentsPanel = ({ form, setForm, errors, setErrors, isEditable }) => {
    const { isInlineCommentsFeatureDisabled } = useApp();
    const { t } = useTranslation('assignment');
    const id = useId();
    const enabled = form.aiInlineCommentsEnabled && !isInlineCommentsFeatureDisabled;
    const disabled = !isEditable || !enabled;
    const invalid =
        enabled && getHTMLWordCount(form.inlineCommentsCustomContext || '') > CUSTOM_CONTENT_WORD_COUNT_LIMIT;
    useClearAssignmentSettingError('inlineCommentsSettings', invalid, errors, setErrors);
    const handleChange = (changes) => {
        if (disabled) return;
        setForm((current) => {
            const value = {
                ...SYSTEM_ASSIGNMENT_DEFAULTS.inlineCommentsSettings,
                ...current.inlineCommentsSettings,
                inlineCommentsCustomContext: current.inlineCommentsCustomContext || '',
            };
            const next = typeof changes === 'function' ? changes(value) : { ...value, ...changes };
            const { inlineCommentsCustomContext, ...inlineCommentsSettings } = next;
            return { ...current, inlineCommentsSettings, inlineCommentsCustomContext };
        });
    };
    return (
        <Stack spacing={5}>
            <SettingsToggleCard
                title={t(
                    'inline-comments-section.automatically-generate-inline-comments',
                    'Automatically generate inline comments?'
                )}
                description={t(
                    'inline-comments-section.inline-comments-description',
                    "Inline comments are distinct from overall feedback. They provide contextual insights directly on the student's work tied to feedback and/or grading suggestions. If this option is disabled, you will still be able to add your own comments manually."
                )}
                checked={enabled}
                disabled={!isEditable}
                restricted={isInlineCommentsFeatureDisabled}
                onChange={(value) => setForm((current) => ({ ...current, aiInlineCommentsEnabled: value }))}
            />
            {invalid && (
                <Text role={'alert'} color={'error'}>
                    {t(
                        'error.additional-instructions-too-long',
                        'Additional instructions exceeds {{wordCount}} words.',
                        { wordCount: CUSTOM_CONTENT_WORD_COUNT_LIMIT }
                    )}
                </Text>
            )}
            <InlineCommentsSettingsForm
                form={{
                    ...SYSTEM_ASSIGNMENT_DEFAULTS.inlineCommentsSettings,
                    ...form.inlineCommentsSettings,
                    inlineCommentsCustomContext: form.inlineCommentsCustomContext || '',
                }}
                handleChange={handleChange}
                disabled={disabled}
                idPrefix={`${id}-`}
            />
        </Stack>
    );
};
export default AssignmentInlineCommentsPanel;
